/*!
 * set-value <https://github.com/jonschlinkert/set-value>
 *
 * Copyright (c) Jon Schlinkert (https://github.com/jonschlinkert).
 * Released under the MIT License.
 */

'use strict';

var deleteProperty = Reflect.deleteProperty;
var isPrimitive = require('is-primitive');
var isPlainObject = require('is-plain-object');
var isObject = function isObject(value) {
  return typeof value === 'object' && value !== null || typeof value === 'function';
};
var isUnsafeKey = function isUnsafeKey(key) {
  return key === '__proto__' || key === 'constructor' || key === 'prototype';
};
var validateKey = function validateKey(key) {
  if (!isPrimitive(key)) {
    throw new TypeError('Object keys must be strings or symbols');
  }
  if (isUnsafeKey(key)) {
    throw new Error("Cannot set unsafe key: \"".concat(key, "\""));
  }
};
var toStringKey = function toStringKey(input) {
  return Array.isArray(input) ? input.flat().map(String).join(',') : input;
};
var createMemoKey = function createMemoKey(input, options) {
  if (typeof input !== 'string' || !options) return input;
  var key = input + ';';
  if (options.arrays !== undefined) key += "arrays=".concat(options.arrays, ";");
  if (options.separator !== undefined) key += "separator=".concat(options.separator, ";");
  if (options.split !== undefined) key += "split=".concat(options.split, ";");
  if (options.merge !== undefined) key += "merge=".concat(options.merge, ";");
  if (options.preservePaths !== undefined) key += "preservePaths=".concat(options.preservePaths, ";");
  return key;
};
var memoize = function memoize(input, options, fn) {
  var key = toStringKey(options ? createMemoKey(input, options) : input);
  validateKey(key);
  var value = setValue.cache.get(key) || fn();
  setValue.cache.set(key, value);
  return value;
};
var splitString = function splitString(input) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var sep = options.separator || '.';
  var preserve = sep === '/' ? false : options.preservePaths;
  if (typeof input === 'string' && preserve !== false && /\//.test(input)) {
    return [input];
  }
  var parts = [];
  var part = '';
  var push = function push(part) {
    var number;
    if (part.trim() !== '' && Number.isInteger(number = Number(part))) {
      parts.push(number);
    } else {
      parts.push(part);
    }
  };
  for (var i = 0; i < input.length; i++) {
    var value = input[i];
    if (value === '\\') {
      part += input[++i];
      continue;
    }
    if (value === sep) {
      push(part);
      part = '';
      continue;
    }
    part += value;
  }
  if (part) {
    push(part);
  }
  return parts;
};
var split = function split(input, options) {
  if (options && typeof options.split === 'function') return options.split(input);
  if (typeof input === 'symbol') return [input];
  if (Array.isArray(input)) return input;
  return memoize(input, options, function () {
    return splitString(input, options);
  });
};
var assignProp = function assignProp(obj, prop, value, options) {
  validateKey(prop);

  // Delete property when "value" is undefined
  if (value === undefined) {
    deleteProperty(obj, prop);
  } else if (options && options.merge) {
    var merge = options.merge === 'function' ? options.merge : Object.assign;

    // Only merge plain objects
    if (merge && isPlainObject(obj[prop]) && isPlainObject(value)) {
      obj[prop] = merge(obj[prop], value);
    } else {
      obj[prop] = value;
    }
  } else {
    obj[prop] = value;
  }
  return obj;
};
var setValue = function setValue(target, path, value, options) {
  if (!path || !isObject(target)) return target;
  var keys = split(path, options);
  var obj = target;
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    var next = keys[i + 1];
    validateKey(key);
    if (next === undefined) {
      assignProp(obj, key, value, options);
      break;
    }
    if (typeof next === 'number' && !Array.isArray(obj[key])) {
      obj = obj[key] = [];
      continue;
    }
    if (!isObject(obj[key])) {
      obj[key] = {};
    }
    obj = obj[key];
  }
  return target;
};
setValue.split = split;
setValue.cache = new Map();
setValue.clear = function () {
  setValue.cache = new Map();
};
module.exports = setValue;